import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { canEditModule } from "@/utils/permissions";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Plus, Edit, Trash2, Search, Tag, Mail } from "lucide-react";

const TablasCategoriasTickets = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = canEditModule(user, "config_tablas_categorias_tkt");

  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nombre: "", descripcion: "", assigned_user_ids: [], notification_email: "",
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchItems();
    axios.get(`${API}/users-list`, { withCredentials: true })
      .then(r => setEmployees(r.data.filter(e => e.is_active !== false)))
      .catch(() => {});
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/ticket-categorias`, { withCredentials: true });
      setItems(res.data);
    } catch {
      toast.error("Error al cargar categorías");
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ nombre: "", descripcion: "", assigned_user_ids: [], notification_email: "" });
    setShowDialog(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      nombre: item.nombre || "",
      descripcion: item.descripcion || "",
      assigned_user_ids: item.assigned_user_ids || [],
      notification_email: item.notification_email || "",
    });
    setShowDialog(true);
  };

  const toggleUser = (uid) => {
    setForm((prev) => {
      const has = prev.assigned_user_ids.includes(uid);
      return {
        ...prev,
        assigned_user_ids: has ? prev.assigned_user_ids.filter(x => x !== uid) : [...prev.assigned_user_ids, uid],
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return toast.error("Nombre obligatorio");
    try {
      if (editing) {
        await axios.put(`${API}/ticket-categorias/${editing.id}`, form, { withCredentials: true });
        toast.success("Categoría actualizada");
      } else {
        await axios.post(`${API}/ticket-categorias`, form, { withCredentials: true });
        toast.success("Categoría creada");
      }
      setShowDialog(false);
      setEditing(null);
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/ticket-categorias/${deleteTarget.id}`, { withCredentials: true });
      toast.success("Categoría eliminada");
      setDeleteTarget(null);
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const nameOf = (uid) => employees.find(e => e.id === uid)?.name || uid;

  const filtered = items.filter((i) =>
    (i.nombre || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (i.descripcion || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="tablas-categorias-tkt-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/configuracion/agencia")} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Tag className="w-6 h-6 text-purple-700" />
            Categorías de Tickets
          </h1>
          <p className="text-sm text-slate-500">Cuando llega un ticket con estas categorías, se notifica a los usuarios asignados automáticamente.</p>
        </div>
        {canEdit && (
          <Button onClick={openNew} className="bg-purple-700 hover:bg-purple-800" data-testid="new-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva categoría
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="mb-4 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar..." className="pl-9" data-testid="search-input" />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Usuarios notificados</TableHead>
                <TableHead>Email grupal</TableHead>
                <TableHead className="w-32 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">Cargando...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">Sin categorías</TableCell></TableRow>
              ) : filtered.map((c) => (
                <TableRow key={c.id} data-testid={`cat-row-${c.id}`}>
                  <TableCell className="font-medium">
                    {c.nombre}
                    {c.descripcion && <p className="text-xs text-slate-400">{c.descripcion}</p>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-sm">
                      {(c.assigned_user_ids || []).length === 0 ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : c.assigned_user_ids.map((uid) => (
                        <Badge key={uid} variant="outline" className="text-[10px]">{nameOf(uid)}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {c.notification_email ? (
                      <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.notification_email}</span>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {canEdit && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(c)} className="text-red-600" data-testid={`delete-${c.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cat-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar categoría" : "Nueva categoría"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required data-testid="cat-name-input" />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={2} data-testid="cat-desc-input" />
            </div>
            <div>
              <Label>Email de notificación grupal</Label>
              <Input
                type="email"
                value={form.notification_email}
                onChange={(e) => setForm({ ...form, notification_email: e.target.value })}
                placeholder="ti@pdtravelmex.com (opcional)"
                data-testid="cat-email-input"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                Usuarios a notificar
                <span className="text-[10px] text-slate-400 font-normal">({form.assigned_user_ids.length} seleccionados)</span>
              </Label>
              <div className="mt-2 max-h-56 overflow-y-auto border rounded-lg divide-y">
                {employees.length === 0 ? (
                  <p className="p-3 text-xs text-slate-400">No hay usuarios</p>
                ) : employees.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 p-2.5 hover:bg-slate-50">
                    <Checkbox
                      id={`cat-emp-${e.id}`}
                      checked={form.assigned_user_ids.includes(e.id)}
                      onCheckedChange={() => toggleUser(e.id)}
                      data-testid={`cat-emp-check-${e.id}`}
                    />
                    <label htmlFor={`cat-emp-${e.id}`} className="flex-1 text-sm cursor-pointer">
                      <span className="font-medium">{e.name}</span>
                      <span className="text-xs text-slate-400 ml-2">{e.email}</span>
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1.5">
                Cada ticket de esta categoría enviará copia a estos usuarios + al correo grupal.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button type="submit" className="bg-purple-700 hover:bg-purple-800" data-testid="cat-save-btn">
                {editing ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar {deleteTarget?.nombre}?</AlertDialogTitle>
            <AlertDialogDescription>Los tickets existentes conservan el nombre en su historial.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TablasCategoriasTickets;
